import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  bestaetigungEntscheiden,
  bestaetigungen as holeVorgaenge,
  type Bestaetigung,
} from '../api/verwaltung';
import { ApiFehler } from '../api/fehler';
import { markenklasse, type Markenart } from '../marken';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

type Stand = 'offen' | 'bestätigt' | 'abgelehnt' | 'verbraucht';

/**
 * Welche Marke ein Stand trägt. Offen verlangt eine Entscheidung (Rost);
 * bestätigt ist noch nicht verbraucht und läuft; verbraucht ist das Ziel.
 * Abgelehnt ist zu Ende, aber nichts, was jetzt noch zu tun wäre.
 */
const STANDMARKE: Readonly<Record<Stand, Markenart>> = {
  offen: 'aufmerksamkeit',
  bestätigt: 'laufend',
  verbraucht: 'abschluss',
  abgelehnt: 'stillgelegt',
};

/**
 * Vier-Augen-Vorgänge (BEN-09).
 *
 * „Eingriffe in die Administratorrolle erfordern die Bestätigung eines
 * zweiten Administrators." Die Regel steht doppelt — im Dienst und als
 * Datenbanktrigger — und ist deshalb auch per SQL nicht zu umgehen. Diese
 * Maske ist der Ort, an dem sie sichtbar wird.
 *
 * WER BEANTRAGT HAT, KANN NICHT BESTÄTIGEN. Das prüft der Server; die Maske
 * blendet die Knöpfe trotzdem aus, wo es offensichtlich ist — ein Knopf, der
 * verlässlich eine Fehlermeldung erzeugt, ist kein Angebot.
 *
 * Seitentyp Liste (ÄA-41): der Zähler im Seitenkopf, der Schalter „Nur
 * offene zeigen" in der Filterleiste, die Vorgänge als Rastertabelle.
 */
export function Bestaetigungen({
  darfEntscheiden,
  eigeneKennung,
}: {
  readonly darfEntscheiden: boolean;
  /** Um wen es sich handelt — wer beantragt hat, entscheidet nicht mit. */
  readonly eigeneKennung: string;
}): JSX.Element {
  const [vorgaenge, setzeVorgaenge] = useState<readonly Bestaetigung[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [nurOffene, setzeNurOffene] = useState(true);
  const [laeuft, setzeLaeuft] = useState(false);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  /* CRM-104: die Entscheidung, nach der das Pop-up gerade fragt. */
  const [nachfrage, setzeNachfrage] = useState<
    { readonly v: Bestaetigung; readonly entscheidung: 'bestaetigen' | 'ablehnen' } | undefined
  >(undefined);

  const laden = useCallback(async (): Promise<void> => {
    setzeVorgaenge(await holeVorgaenge(nurOffene));
  }, [nurOffene]);

  useEffect(() => {
    setzeLaedt(true);
    void laden()
      .catch((ursache: unknown) => setzeFehler(ursache as ApiFehler))
      .finally(() => setzeLaedt(false));
  }, [laden]);

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(
    laedt
      ? undefined
      : nurOffene
        ? zaehlwort(vorgaenge.length, 'offener Vorgang', 'offene Vorgänge')
        : zaehlwort(vorgaenge.length, 'Vorgang', 'Vorgänge'),
  );

  /*
   * Aus der Rückfrage heraus (CRM-104): Scheitert die Entscheidung, wirft
   * `entscheide` — der Fehler steht im Pop-up, das offen bleibt, und nicht
   * noch einmal auf der Seite. Gelingt sie, schließt das Pop-up; ein Fehler
   * beim Neuladen danach gehört auf die Seite.
   */
  async function entscheide(v: Bestaetigung, entscheidung: 'bestaetigen' | 'ablehnen'): Promise<void> {
    setzeLaeuft(true);
    try {
      await bestaetigungEntscheiden(v.id, entscheidung);
      setzeFehler(undefined);
      setzeNachfrage(undefined);
      setzeRueckmeldung(
        entscheidung === 'bestaetigen'
          ? 'Bestätigt. Die Rolle lässt sich jetzt in der Benutzerakte '
            + 'setzen — der Vorgang wird dabei verbraucht.'
          : 'Abgelehnt.',
      );
    } finally {
      setzeLaeuft(false);
    }
    await laden().catch((ursache: unknown) => setzeFehler(ursache as ApiFehler));
  }

  const zeit = (wert: string | null): string =>
    wert === null ? '—' : new Date(wert).toLocaleString('de-DE');

  /** Offen heisst: weder bestätigt noch abgelehnt noch verbraucht. */
  const istOffen = (v: Bestaetigung): boolean =>
    v.bestaetigtAm === null && v.abgelehntAm === null && v.verbrauchtAm === null;

  const stand = (v: Bestaetigung): Stand => {
    if (v.verbrauchtAm !== null) return 'verbraucht';
    if (v.abgelehntAm !== null) return 'abgelehnt';
    if (v.bestaetigtAm !== null) return 'bestätigt';
    return 'offen';
  };

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      {/* BEN-09 */}
      <Liste
        filter={
          <label className="liste__schalter">
            <input
              type="checkbox"
              checked={nurOffene}
              onChange={(e) => setzeNurOffene(e.target.checked)}
            />
            Nur offene zeigen
          </label>
        }
        marken={nurOffene ? [{ text: 'nur offene', entfernen: () => setzeNurOffene(false) }] : []}
        hinweise={
          <p className="hilfe">
            Eine Bestätigung wird <strong>verbraucht</strong>, sobald die Rolle damit gesetzt
            wurde. Sie gilt also für einen Vorgang und nicht als Dauererlaubnis.
          </p>
        }
      >
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">Vier-Augen-Vorgänge</caption>
            <thead>
              <tr>
                <th scope="col">Betrifft</th>
                <th scope="col">Handlung</th>
                <th scope="col">Beantragt</th>
                <th scope="col">Grund</th>
                <th scope="col">Stand</th>
                <th scope="col">Entscheidung</th>
              </tr>
            </thead>
            <tbody>
              {vorgaenge.map((v) => (
                <tr key={v.id}>
                  <td className="liste__name">{v.betroffenerName}</td>
                  <td>
                    {v.handlung === 'zuweisen' ? 'Rolle zuweisen' : 'Rolle entziehen'}
                    <span className="kanalliste__name" title={v.gegenstand}> · {v.gegenstandName}</span>
                  </td>
                  <td>
                    {v.beantragtVonName}
                    <span className="kanalliste__name"> · {zeit(v.beantragtAm)}</span>
                  </td>
                  <td>{v.grund}</td>
                  <td>
                    <span className={markenklasse(STANDMARKE[stand(v)])}>{stand(v)}</span>
                  </td>
                  <td>
                    {/*
                      Wer beantragt hat, entscheidet nicht mit — sonst wäre
                      das Vier-Augen-Prinzip ein Zwei-Augen-Prinzip mit einem
                      Umweg. Der Server weist es ab; hier fehlt der Knopf
                      schon, damit niemand darauf drückt.
                    */}
                    {darfEntscheiden && istOffen(v) && v.beantragtVon !== eigeneKennung && (
                      <div className="knopfreihe">
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          disabled={laeuft}
                          onClick={() => setzeNachfrage({ v, entscheidung: 'bestaetigen' })}
                        >
                          Bestätigen …
                        </button>
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          disabled={laeuft}
                          onClick={() => setzeNachfrage({ v, entscheidung: 'ablehnen' })}
                        >
                          Ablehnen …
                        </button>
                      </div>
                    )}
                    {istOffen(v) && v.beantragtVon === eigeneKennung && (
                      <span className="kanalliste__name">
                        Sie haben beantragt — eine zweite Person entscheidet.
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!laedt && vorgaenge.length === 0 && (
          <div className="liste__leer" role="status">
            <p>
              {nurOffene
                ? 'Kein Vorgang wartet auf eine Entscheidung.'
                : 'Es gibt keine Vorgänge.'}
            </p>
          </div>
        )}
      </Liste>

      {/*
        BEN-09: Die zweite Person entscheidet. Der Grund steht im Antrag und
        wird hier nur genannt; die Bestätigung gilt für diesen einen Vorgang.
      */}
      {nachfrage !== undefined && (
        <Rueckfrage
          frage={nachfrage.entscheidung === 'bestaetigen' ? 'Antrag bestätigen?' : 'Antrag ablehnen?'}
          unterzeile={`${nachfrage.v.gegenstandName} · ${nachfrage.v.betroffenerName}`}
          tat={nachfrage.entscheidung === 'bestaetigen' ? 'Bestätigen' : 'Ablehnen'}
          beiBestaetigen={() => entscheide(nachfrage.v, nachfrage.entscheidung)}
          beiSchliessen={() => setzeNachfrage(undefined)}
        >
          <p>
            {nachfrage.v.beantragtVonName} hat beantragt, {nachfrage.v.betroffenerName} die Rolle
            „{nachfrage.v.gegenstandName}“{' '}
            {nachfrage.v.handlung === 'zuweisen' ? 'zuzuweisen' : 'zu entziehen'}.{' '}
            {nachfrage.entscheidung === 'bestaetigen'
              ? 'Danach lässt sich die Rolle in der Benutzerakte einmal setzen.'
              : 'Der Antrag endet; ein neuer Versuch braucht einen neuen Antrag.'}
          </p>
          <p>Grund: {nachfrage.v.grund}</p>
        </Rueckfrage>
      )}
    </>
  );
}
